import React, { useEffect, useState, Children } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Sparkles, Clock, Heart, Home, Leaf, DollarSign, CheckCircle2, Star, Shield, Download, Zap, Gift } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
const BOOK_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/1VY3ZhsNYKKW7UpRU8xbwG/ChatGPT_Image_10_dic_2025,_21_43_48.png';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LandingPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const {
    scrollY
  } = useScroll();
  const checkoutUrl = 'https://www.grupogeo0610.com/cart/52830717215085:1';
  useEffect(() => {
    const unsubscribe = scrollY.on('change', latest => {
      setShowStickyCTA(latest > 600);
    });
    return () => unsubscribe();
  }, [scrollY]);
  return <div className="min-h-screen overflow-x-hidden" data-id="element-6">
      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-7">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-8">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center md:text-left z-10" data-id="element-9">
            <span className="inline-block py-1 px-3 rounded-full bg-vintage-sage/20 text-vintage-dark text-sm font-semibold mb-6 tracking-wider uppercase" data-id="element-10">
              Edición Especial
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 text-vintage-dark" data-id="element-11">
              SABIDURÍA <br data-id="element-12" />
              <span className="text-vintage-brown" data-id="element-13">COTIDIANA 2.0</span>
            </h1>
            <p className="text-xl md:text-2xl text-vintage-text/90 mb-8 font-light italic" data-id="element-14">
              El arte moderno de vivir mejor con los trucos, remedios y consejos
              eternos de la abuela.
            </p>
            <CTAButton href={checkoutUrl} className="w-full md:w-auto" data-id="element-15">
              Obtén Sabiduría Cotidiana 2.0
            </CTAButton>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          rotate: -2
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="relative mx-auto md:mx-0 max-w-sm md:max-w-md" data-id="element-16">
            <div className="absolute inset-0 bg-vintage-brown/20 blur-3xl rounded-full transform translate-y-10" data-id="element-17"></div>
            <img src={BOOK_IMAGE_URL} alt="Portada del libro Sabiduría Cotidiana 2.0" className="relative z-10 w-full drop-shadow-2xl rounded-lg transform hover:scale-105 transition-transform duration-500" data-id="element-18" />
          </motion.div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-white/50" data-id="element-19">
        <div className="max-w-3xl mx-auto text-center" data-id="element-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-21">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-vintage-dark" data-id="element-22">
              La vida no tiene que ser tan complicada… y tú lo sabes.
            </h2>
            <p className="text-lg text-vintage-text mb-8 leading-relaxed" data-id="element-23">
              Si eres una mujer entre 20 y 45 años, seguramente esta escena te
              resulta demasiado familiar:
            </p>
            <div className="bg-vintage-cream p-8 rounded-2xl shadow-inner mb-10 border border-vintage-brown/10" data-id="element-24">
              <p className="text-lg italic text-vintage-dark/80" data-id="element-25">
                "El trabajo te exige. La casa nunca se detiene. La cocina te
                espera. Los niños demandan tu atención. La pareja también. Los
                pendientes se acumulan. El tiempo nunca alcanza."
              </p>
            </div>
            <p className="text-xl font-medium text-vintage-brown mb-4" data-id="element-26">
              Y cuando finalmente te sientas un momento, aparece ese pensamiento
              silencioso:
            </p>
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-vintage-dark" data-id="element-27">
              "¿Por qué todo requiere tanto esfuerzo?"
            </h3>
          </motion.div>
        </div>
      </section>

      {/* Agitation Section */}
      <section className="py-20 px-4 bg-vintage-dark text-vintage-cream" data-id="element-28">
        <div className="max-w-3xl mx-auto text-center" data-id="element-29">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-30">
            <div className="w-16 h-1 bg-vintage-sage mx-auto mb-8" data-id="element-31"></div>
            <p className="text-2xl md:text-3xl font-serif leading-relaxed mb-8" data-id="element-32">
              No es tu culpa. No te falta capacidad. No eres desorganizada.
            </p>
            <p className="text-xl md:text-2xl font-light text-vintage-cream/90" data-id="element-33">
              La verdad es simple:{' '}
              <strong className="text-vintage-sage" data-id="element-34">
                Nunca te enseñaron los trucos que hacen la vida fácil.
              </strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Intro */}
      <section className="py-20 px-4" data-id="element-35">
        <div className="max-w-4xl mx-auto" data-id="element-36">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-37">
            <div data-id="element-38">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-vintage-dark" data-id="element-39">
                Las mujeres de antes no tenían más tiempo… tenían más sabiduría.
              </h2>
              <p className="text-lg text-vintage-text mb-6" data-id="element-40">
                Tu abuela, tu bisabuela, las mujeres que vinieron antes de ti…
              </p>
              <p className="text-lg text-vintage-text mb-6" data-id="element-41">
                No tenían robots aspiradores, productos especializados para cada
                cosa ni hacks de internet. Tenían algo mejor:{' '}
                <span className="font-semibold text-vintage-brown" data-id="element-42">
                  Conocían micro-hábitos diarios que mantenían el hogar
                  impecable sin estrés ni gastos innecesarios.
                </span>
              </p>
              <p className="text-lg text-vintage-text" data-id="element-43">
                Sabían cómo eliminar olores con ingredientes comunes. Cómo
                resolver problemas cotidianos en segundos. Cómo mantener un
                hogar acogedor sin complicaciones.
              </p>
            </div>
            <div className="relative" data-id="element-44">
              <div className="absolute inset-0 bg-vintage-sage/20 rounded-full blur-2xl" data-id="element-45"></div>
              <div className="relative bg-white p-8 rounded-xl shadow-xl border border-vintage-cream rotate-2" data-id="element-46">
                <h3 className="text-xl font-serif font-bold text-vintage-dark mb-4 text-center" data-id="element-47">
                  Lo que ellas sabían
                </h3>
                <ul className="space-y-4" data-id="element-48">
                  {['Vivir la vida con más calma', 'Organización práctica', 'Trucos que realmente funcionan', 'Sabiduría femenina que no falla'].map((item, i) => <li key={i} className="flex items-center text-vintage-text" data-id="element-49">
                      <Star className="w-5 h-5 text-vintage-brown mr-3 flex-shrink-0" data-id="element-50" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Description */}
      <section className="py-20 px-4 bg-vintage-sage/10" data-id="element-51">
        <div className="max-w-4xl mx-auto text-center" data-id="element-52">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-53">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-vintage-dark" data-id="element-54">
              Por eso nació <br data-id="element-55" />
              <span className="text-vintage-brown" data-id="element-56">
                Sabiduría Cotidiana 2.0
              </span>
            </h2>
            <p className="text-xl text-vintage-text mb-12 max-w-2xl mx-auto" data-id="element-57">
              Los trucos eternos de las mujeres sabias… actualizados para la
              mujer moderna.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-id="element-58">
              {['Mamá joven con poco tiempo', 'Profesional que hace mil cosas', 'Mujer que vive sola', 'Encargada del hogar'].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-md border-b-4 border-vintage-sage" data-id="element-59">
                  <p className="font-medium text-vintage-dark" data-id="element-60">{item}</p>
                </div>)}
            </div>

            <p className="text-lg text-vintage-text mb-8" data-id="element-61">
              Todas tienen algo en común: Quieren una vida más simple, más
              ordenada y más manejable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4" data-id="element-62">
        <div className="max-w-5xl mx-auto" data-id="element-63">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-64">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-vintage-dark" data-id="element-65">
              Imagina por un momento:
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-66">
              {[{
              icon: Clock,
              text: 'Limpiar tu cocina en minutos'
            }, {
              icon: Leaf,
              text: 'Aromatizar tu casa sin químicos'
            }, {
              icon: Sparkles,
              text: 'Quitar manchas difíciles con ingredientes de la alacena'
            }, {
              icon: Home,
              text: 'Tener un hogar siempre fresco y bonito'
            }, {
              icon: DollarSign,
              text: 'Ahorrar dinero cada mes'
            }, {
              icon: Heart,
              text: 'Cuidar a tu familia con remedios simples'
            }, {
              icon: CheckCircle2,
              text: 'Sentirte capaz, segura y tranquila'
            }, {
              icon: Star,
              text: 'Ganar tiempo para ti'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex items-start p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-vintage-cream" data-id="element-67">
                  <div className="bg-vintage-cream p-3 rounded-full mr-4" data-id="element-68">
                    <benefit.icon className="w-6 h-6 text-vintage-brown" data-id="element-69" />
                  </div>
                  <p className="text-lg text-vintage-dark font-medium pt-1" data-id="element-70">
                    {benefit.text}
                  </p>
                </motion.div>)}
            </div>

            <div className="mt-16 text-center" data-id="element-71">
              <p className="text-xl md:text-2xl font-serif text-vintage-brown mb-8" data-id="element-72">
                Eso es exactamente lo que empezarás a sentir cuando uses
                Sabiduría Cotidiana 2.0.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Micro-habits Section */}
      <section className="py-20 px-4 bg-vintage-dark text-vintage-cream relative overflow-hidden" data-id="element-73">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/flower-trail.png')]" data-id="element-74"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-75">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center" data-id="element-76">
            <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-77">
              El secreto son los micro-hábitos femeninos ancestrales
            </h2>
            <p className="text-xl mb-12 font-light" data-id="element-78">
              Pequeñas acciones, rápidas y simples, que transforman tu hogar y
              tu energía diaria.
            </p>

            <div className="bg-vintage-cream/5 backdrop-blur-sm p-8 rounded-2xl border border-vintage-cream/20" data-id="element-79">
              <h3 className="text-2xl font-serif mb-6 text-vintage-sage" data-id="element-80">
                Dentro de Sabiduría Cotidiana 2.0 encontrarás:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left" data-id="element-81">
                {['Más de 200 trucos prácticos', 'Soluciones para cocina, limpieza y organización', 'Aromas, bienestar y remedios naturales', 'Trucos económicos (sin productos caros)', 'Resultados desde el primer día', 'Consejos simples, claros y fáciles de aplicar'].map((item, i) => <div key={i} className="flex items-center" data-id="element-82">
                    <CheckCircle2 className="w-5 h-5 text-vintage-sage mr-3 flex-shrink-0" data-id="element-83" />
                    <span data-id="element-84">{item}</span>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 bg-vintage-cream" data-id="element-85">
        <div className="max-w-3xl mx-auto" data-id="element-86">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="prose prose-lg prose-stone mx-auto" data-id="element-87">
            <h2 className="text-3xl font-bold text-center mb-10 text-vintage-dark font-serif" data-id="element-88">
              La historia que inició todo fue muy simple
            </h2>

            <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg border-l-4 border-vintage-brown relative" data-id="element-89">
              <span className="absolute top-4 left-4 text-6xl text-vintage-brown/20 font-serif" data-id="element-90">
                "
              </span>
              <p className="mb-6 italic text-vintage-text" data-id="element-91">
                Un día, después de un caos doméstico, le conté a mi abuela que
                no lograba quitar un olor de la casa. Ella solo dijo:{' '}
                <span className="font-semibold text-vintage-dark" data-id="element-92">
                  "Pon un vasito de vinagre ahí, verás."
                </span>{' '}
                Lo hice. Y funcionó en minutos.
              </p>
              <p className="mb-6 text-vintage-text" data-id="element-93">
                Ese día entendí algo profundo: Las mujeres de antes resolvían
                todo sin estrés porque sabían cosas que nosotras olvidamos.
              </p>
              <p className="mb-6 text-vintage-text" data-id="element-94">
                Ella me dijo una frase que me marcó:
              </p>
              <p className="text-2xl font-serif font-bold text-center text-vintage-brown my-8" data-id="element-95">
                "Ustedes se complican mucho. La vida es más simple."
              </p>
              <p className="text-vintage-text" data-id="element-96">
                Desde entonces, me dediqué a recopilar esa sabiduría perdida y
                actualizarla para mujeres como tú. Así nació Sabiduría Cotidiana
                2.0.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-20 px-4 bg-vintage-sage/20" data-id="element-97">
        <div className="max-w-4xl mx-auto text-center" data-id="element-98">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-99">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-vintage-dark" data-id="element-100">
              Y hoy quiero darte una promesa audaz, pero real:
            </h2>
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-vintage-brown/20 transform md:scale-110 transition-transform" data-id="element-101">
              <p className="text-xl md:text-2xl font-serif leading-relaxed text-vintage-dark" data-id="element-102">
                En solo{' '}
                <span className="text-vintage-brown font-bold" data-id="element-103">7 días</span>{' '}
                sentirás tu vida más organizada, más ligera y más fácil…
                aplicando los mismos trucos simples que usaron las mujeres
                sabias durante generaciones.
              </p>
            </div>
            <p className="mt-12 text-lg text-vintage-text" data-id="element-104">
              Sabiduría Cotidiana 2.0 no solo transforma tu hogar. Transforma tu
              tiempo, tu energía, tu bienestar y tu paz mental.
            </p>
            <p className="mt-4 text-xl font-medium text-vintage-dark" data-id="element-105">
              Es la guía que te permite decir: "Por fin tengo el control de mi
              vida diaria."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-vintage-dark text-vintage-cream relative overflow-hidden" data-id="element-106">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/old-map.png')]" data-id="element-107"></div>
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center" data-id="element-109">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-vintage-cream" data-id="element-110">
              Acceso Inmediato Por Solo
            </h2>

            <div className="my-12" data-id="element-111">
              <div className="inline-block bg-vintage-cream text-vintage-dark p-8 md:p-12 rounded-2xl shadow-2xl border-4 border-vintage-sage/30 transform hover:scale-105 transition-transform duration-300" data-id="element-112">
                <div className="flex items-center justify-center gap-4 mb-4" data-id="element-113">
                  <span className="text-2xl md:text-3xl font-serif line-through opacity-50" data-id="element-114">
                    $29.97
                  </span>
                  <span className="bg-vintage-brown text-vintage-cream px-4 py-2 rounded-full text-sm font-bold" data-id="element-115">
                    67% OFF
                  </span>
                </div>
                <div className="flex items-start justify-center" data-id="element-116">
                  <span className="text-3xl md:text-4xl font-serif mt-2" data-id="element-117">
                    $
                  </span>
                  <span className="text-7xl md:text-8xl font-bold font-serif" data-id="element-118">
                    9
                  </span>
                  <span className="text-4xl md:text-5xl font-serif mt-2" data-id="element-119">
                    .97
                  </span>
                </div>
                <p className="text-vintage-text mt-4 text-lg" data-id="element-120">
                  Pago único • Acceso de por vida
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12" data-id="element-121">
              <div className="order-2 md:order-1" data-id="element-122">
                
              </div>

              <div className="order-1 md:order-2 text-left" data-id="element-123">
                <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-vintage-sage" data-id="element-124">
                  Esto es lo que obtienes hoy:
                </h3>
                <ul className="space-y-4" data-id="element-125">
                  {[{
                  icon: CheckCircle2,
                  text: '200+ trucos prácticos probados'
                }, {
                  icon: Download,
                  text: 'Acceso inmediato en formato digital'
                }, {
                  icon: Zap,
                  text: 'Resultados desde el primer día'
                }, {
                  icon: Shield,
                  text: 'Garantía de satisfacción 30 días'
                }, {
                  icon: Star,
                  text: 'Actualizaciones gratuitas de por vida'
                }, {
                  icon: Heart,
                  text: 'Soporte por email incluido'
                }].map((item, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start" data-id="element-126">
                      <item.icon className="w-6 h-6 text-vintage-sage mr-3 flex-shrink-0 mt-1" data-id="element-127" />
                      <span className="text-lg" data-id="element-128">{item.text}</span>
                    </motion.li>)}
                </ul>
              </div>
            </div>

            {/* Bonus Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="mb-12" data-id="element-129">
              <div className="flex items-center justify-center gap-3 mb-6" data-id="element-130">
                <Gift className="w-8 h-8 text-vintage-sage" data-id="element-131" />
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-vintage-cream" data-id="element-132">
                  Bonos Gratis Incluidos
                </h3>
                <Gift className="w-8 h-8 text-vintage-sage" data-id="element-133" />
              </div>

              <div className="bg-gradient-to-br from-vintage-sage/20 to-vintage-cream/10 p-6 md:p-8 rounded-2xl border-2 border-vintage-sage/30 relative overflow-hidden" data-id="element-134">
                {/* "GRATIS" badge */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center" data-id="element-135">
                  <div className="order-2 md:order-1" data-id="element-136">
                    <div className="bg-vintage-brown/10 px-3 py-1 rounded-full inline-block mb-4" data-id="element-137">
                      <span className="text-vintage-brown font-bold text-sm" data-id="element-138">
                        BONO #1
                      </span>
                    </div>
                    <h4 className="text-xl md:text-2xl font-serif font-bold text-vintage-cream mb-4" data-id="element-139">
                      Mini-Guía "La Casa Fresca en 5 Minutos"
                    </h4>
                    <p className="text-vintage-cream/90 text-lg leading-relaxed" data-id="element-140">
                      Aromatiza, ventila y renueva la energía de tu hogar en
                      tiempo récord. Trucos exprés perfectos para cuando llega
                      visita o necesitas que tu casa huela y se sienta
                      impecable…{' '}
                      <span className="font-bold text-vintage-sage" data-id="element-141">
                        ¡en solo 5 minutos!
                      </span>
                    </p>
                    <div className="mt-4 flex items-center text-vintage-sage" data-id="element-142">
                      <Sparkles className="w-5 h-5 mr-2" data-id="element-143" />
                      <span className="font-medium" data-id="element-144">
                        Valor: $7.97 • Hoy: GRATIS
                      </span>
                    </div>
                  </div>

                  <div className="order-1 md:order-2" data-id="element-145">
                    <img src="https://cdn.magicpatterns.com/uploads/p7zYajoLFvG9HLs1cPj7po/ChatGPT_Image_11_dic_2025,_02_35_09.png" alt="Bono: La Casa Fresca en 5 Minutos" className="w-full rounded-xl shadow-2xl border-4 border-vintage-cream/30 transform hover:scale-105 transition-transform duration-300" data-id="element-146" />
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="bg-vintage-cream/10 backdrop-blur-sm p-8 rounded-2xl border border-vintage-cream/20 mb-8" data-id="element-147">
              <p className="text-xl md:text-2xl font-serif mb-6" data-id="element-148">
                <span className="text-vintage-sage font-bold" data-id="element-149">
                  Garantía de 30 días:
                </span>{' '}
                Si no ves resultados reales en tu hogar, te devolvemos cada
                centavo. Sin preguntas.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm opacity-80" data-id="element-150">
                <span className="flex items-center" data-id="element-151">
                  <Shield className="w-4 h-4 mr-2" data-id="element-152" />
                  Pago seguro
                </span>
                <span className="flex items-center" data-id="element-153">
                  <Download className="w-4 h-4 mr-2" data-id="element-154" />
                  Descarga instantánea
                </span>
                <span className="flex items-center" data-id="element-155">
                  <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-156" />
                  Sin suscripciones
                </span>
              </div>
            </div>

            <CTAButton href={checkoutUrl} className="text-xl px-12 py-6 transform hover:scale-105" data-id="element-157">
              Sí, quiero mi guía ahora por $9.97
            </CTAButton>

            <p className="mt-6 text-sm opacity-70" data-id="element-158">
              Más de 5,000 mujeres ya están transformando sus hogares
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 pb-40" data-id="element-159">
        <div className="max-w-4xl mx-auto text-center" data-id="element-160">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-161">
            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-vintage-dark" data-id="element-162">
              Tu vida puede cambiar con un truco… <br data-id="element-163" />
              <span className="text-vintage-brown" data-id="element-164">o con 200.</span>
            </h2>

            <div className="flex flex-col items-center justify-center gap-8 mb-12" data-id="element-165">
              <img src={BOOK_IMAGE_URL} alt="Sabiduría Cotidiana 2.0" className="w-64 md:w-80 shadow-2xl rounded-lg rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-166" />
              <CTAButton href={checkoutUrl} className="text-xl px-10 py-5" data-id="element-167">
                👉 Obtén Sabiduría Cotidiana 2.0 ahora mismo
              </CTAButton>
            </div>

            <p className="text-lg text-vintage-text/80 italic" data-id="element-168">
              Descubre la forma más hermosa, práctica y económica de hacerte la
              vida más fácil.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-vintage-dark text-vintage-cream py-8 text-center" data-id="element-169">
        <p className="text-sm opacity-60" data-id="element-170">
          © {new Date().getFullYear()} Sabiduría Cotidiana. Todos los derechos
          reservados.
        </p>
      </footer>

      {/* Sticky Mobile CTA */}
      <AnimatePresence data-id="element-171">
        {showStickyCTA && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-6 left-4 right-4 z-50 md:hidden" data-id="element-172">
            <CTAButton variant="sticky" fullWidth href={checkoutUrl} className="shadow-2xl border border-vintage-cream/20" data-id="element-173">
              Obtén la guía ahora
            </CTAButton>
          </motion.div>}
      </AnimatePresence>
    </div>;
}